/**
 * RegulatoryViewerEmbed — Family-parameterised document viewer.
 *
 * Single source of truth for the embeddable page-image viewer used
 * by every regulatory family (SBA, EPAR, UK PIL, ...).  Mirrors the
 * backend's `regulatory_viewer_routes.build_viewer_router(family)`
 * pattern: one component, parameterised by the URL prefix that maps
 * to a family.
 *
 * Replaces the previous per-family SBAViewerEmbed and EPARViewerEmbed
 * (each ~680 lines, 95% identical).  Pre-existing call sites continue
 * to work via the thin wrappers in `SBAViewerEmbed.tsx`,
 * `EPARViewerEmbed.tsx`, and `UKPILViewerEmbed.tsx` which all
 * re-export this component with a family-bound `viewerApi` prop.
 *
 * Props:
 *  - docId: document ID within the family's table
 *  - viewerApi: the family-bound API client (one of `sbaApi`,
 *    `eparApi`, `ukPilApi` from `createViewerApi.ts`); used for
 *    every backend call this component makes
 *  - highlightChunkIds: chunk IDs to highlight (from CEPI citations)
 *  - onChunkClick: callback when user clicks a chunk highlight
 *
 * The `viewerApi.prefix` (e.g. '/sba', '/epar', '/uk_pil') is used
 * only in log labels — every actual URL is built by the API client.
 */
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Loader2, AlertCircle, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import type {
  TOCEntry,
  PageInfo,
  TextSpan,
  ChunkMetadata,
  SearchResponse,
  SearchResult,
} from '../../types/viewer';
import type { ViewerApi } from '../../api/createViewerApi';
import { maxPageNumOf, pageNumsForSections } from '../../utils/pdfSectionPages';
export { maxPageNumOf, pageNumsForSections };
import { ANCHOR_TEXT_MAX } from './anchorBbox';
import TOCSidebar from './TOCSidebar';
import DocumentSearch from './DocumentSearch';
import PageRenderer, { type AutoSelect } from './PageRenderer';
import PageZoomLightbox from './PageZoomLightbox';
import DisplayOnlyPageFrame from './DisplayOnlyPageFrame';

// Stable empty array for the ``chunks`` prop on pages with no chunks.
// Using an inline ``?? []`` handed PageRenderer a fresh array every render,
// defeating React.memo for every chunk-less page (nearly all pages on a JP
// interview form). One shared frozen constant keeps the prop identity stable.
const EMPTY_CHUNKS: ChunkMetadata[] = [];

// Overlay virtualization. Above OVERLAY_VIRT_THRESHOLD pages, the per-page
// text/hover overlays are mounted only for pages within OVERLAY_WINDOW of the
// current page — the rest render just their lazy <img>. This caps the DOM at
// ~(2·window+1) pages of overlay nodes instead of the whole doc (a 427-page JP
// interview form otherwise mounts ~99k <span>s at once → scroll/hover jank).
// Small docs stay fully rendered so native ⌘F keeps working there.
const OVERLAY_VIRT_THRESHOLD = 60;
const OVERLAY_WINDOW = 3;

/**
 * Should THIS viewer instance intercept ⌘F/Ctrl+F and redirect it to its own
 * in-document search box?  Two conditions:
 *   1. the doc is virtualized (pageCount > threshold) — otherwise native Find
 *      still works because every page's text is in the DOM, so we leave it alone;
 *   2. the user is actually IN this viewer — the pointer is over it, or focus is
 *      already inside it.  The ⌘F handler is a global window listener and the
 *      compare view can mount several viewers at once; without this gate every
 *      instance would fire and the last-registered one would steal focus into the
 *      wrong pane. When the user is outside every viewer, none intercept and the
 *      browser's native Find is left untouched.
 * Pure + exported so the boundary (60 vs 61 pages) and the multi-viewer scoping
 * can be unit-tested without a full component render.
 */
export function shouldViewerHandleFind(
  pageCount: number,
  viewerIsActive: boolean,
  threshold: number = OVERLAY_VIRT_THRESHOLD,
): boolean {
  return pageCount > threshold && viewerIsActive;
}

// ─────────────────────────────────────────────────────────────────────
// Client-side (Ctrl+F-style) in-document search helpers
// ─────────────────────────────────────────────────────────────────────

/** Escape a raw string for safe insertion as HTML text. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Build a highlighted excerpt for a matched line, with the match wrapped in
 *  ``<mark>`` (DocumentSearch renders this via dangerouslySetInnerHTML and
 *  styles ``mark`` yellow). Everything is HTML-escaped before the <mark> is
 *  injected, so document text can never inject markup.
 *
 *  LEADS WITH THE MATCH: only a short pre-context, longer post-context — so the
 *  highlighted term lands inside the 2-line clamped preview even when it sits
 *  late in a long line. A large leading window pushed the term past the
 *  line-clamp and it never showed in the result list. */
/** Case- and accent-insensitive comparison form for Latin text (ä→a, é→e).
 *  One output unit per input unit, so an index into the folded string is an
 *  index into the original — the headline / page box mark the ORIGINAL word.
 *  ponytail: ß is left as-is (ß↔ss would shift indexes); add a two-way map if
 *  users report Swiss "ss" vs German "ß" misses. */
export function foldLatin(s: string): string {
  let out = '';
  for (const c of s.split('')) {
    const f = c.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    out += f.length === 1 ? f : c;
  }
  return out;
}

function buildHeadline(text: string, query: string,
                       fold: (s: string) => string = (x) => x.toLowerCase()): string {
  const lower = fold(text);
  const q = fold(query);
  const i = lower.indexOf(q);
  if (i < 0) return escapeHtml(text.slice(0, 160));
  const PRE = 18;
  const POST = 140;
  let start = Math.max(0, i - PRE);
  if (start > 0) {
    const sp = text.indexOf(' ', start);
    if (sp >= 0 && sp < i) start = sp + 1; // begin at the next word boundary, not mid-word
  }
  const end = Math.min(text.length, i + q.length + POST);
  const pre = (start > 0 ? '…' : '') + escapeHtml(text.slice(start, i));
  const mid = escapeHtml(text.slice(i, i + q.length));
  const post = escapeHtml(text.slice(i + q.length, end)) + (end < text.length ? '…' : '');
  return `${pre}<mark>${mid}</mark>${post}`;
}

// ─────────────────────────────────────────────────────────────────────
// Public handle for parent component (DrugDetailPage) to control viewer
// ─────────────────────────────────────────────────────────────────────

export interface RegulatoryViewerHandle {
  scrollToPage: (pageNum: number) => void;
  highlightChunks: (chunkIds: number[]) => void;
  /** Scrolls to the first section in the current doc whose
   *  ``section_number`` equals ``sectionNumber`` (exact match
   *  with trailing-period + whitespace trim).  Returns ``true``
   *  when a match was found in the current doc's TOC and the
   *  scroll fired; ``false`` when no match — the caller can
   *  fall back to Phase 10b's bundle-wide fuzzy resolver.
   *  ``allowSubsectionPrefix`` (Compare universal-section picker only)
   *  additionally lands on the first subsection when the exact parent
   *  number is absent (4.4 → 4.4.1); leave it off to keep exact-only.
   *  ``deferUntilLoaded`` (Compare) accepts a call made while the document is
   *  still loading — returns ``true`` and replays it once the TOC arrives —
   *  instead of reporting a miss against the not-yet-loaded TOC. Off by
   *  default so callers that fall back to the resolver on ``false`` keep it. */
  scrollToSection: (
    sectionNumber: string,
    opts?: ScrollToSectionOpts,
  ) => boolean;
}

export interface ScrollToSectionOpts {
  allowSubsectionPrefix?: boolean;
  deferUntilLoaded?: boolean;
}

// Backwards-compatible alias — pre-refactor code imported
// `SBAViewerHandle` from `SBAViewerEmbed.tsx`.  Keep the type
// name available so the same imports keep type-checking once
// `SBAViewerEmbed.tsx` re-exports from here.
export type { RegulatoryViewerHandle as SBAViewerHandle };

// ─────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────

/**
 * Per-chunk anchor phrases for sentence-level highlight tightening.
 * When a citation's chunk has an entry here, ``PageRenderer`` matches
 * the phrase against the page's text_spans and draws a tight bbox
 * around only the matching visual line(s) instead of the full chunk
 * paragraph rectangle.  Missing entries (or no match) fall back to
 * ``chunk.bbox`` — no regression.
 */
export interface ChunkAnchor {
  start: string;
  end?: string | null;
  /** The WHOLE cited sentence, when we have it — not just the 60-character
   *  head and tail above.
   *
   *  The four strict matching passes deliberately use the short anchors: a long
   *  exact needle dies on a single OCR error, which is why the anchors are
   *  short in the first place. The fifth pass needs the opposite — a whole
   *  sentence is what lets a character-level comparison separate two table rows
   *  that differ only in a dose, because `Dose 1` fits both and
   *  `Dose 1.5 mg once daily` fits one. See `sentenceFindRange`.
   *
   *  Optional, and absent on anchors replayed from an older cached answer or an
   *  older URL. The fifth pass simply does not run then, so those keep exactly
   *  the behaviour they had. */
  text?: string | null;
}

/** One per-page section box: 1-based ``page`` + normalized [x0,y0,x1,y1]. Used
 *  by the EU-historical citation path, which has no chunk_ids — the box is drawn
 *  as an overlay and the first box is the scroll target. */
export interface SectionSpan {
  page: number;
  box: [number, number, number, number];
}

/** True when ``sectionNumber`` (trailing-period tolerant) is an exact section in
 *  ``tocEntries`` — the same match rule ``scrollToSection`` uses. Pure/testable. */
export function sectionInToc(
  tocEntries: { section_number?: string | null }[],
  sectionNumber: string,
): boolean {
  const trimmed = sectionNumber.trim().replace(/\.$/, '');
  if (!trimmed) return false;
  return tocEntries.some((e) => e.section_number === trimmed);
}

/** Resolve ``sectionNumber`` to the TOC entry ``scrollToSection`` should land on.
 *  Exact match wins (earliest by ``page_start``). ONLY when no exact match AND
 *  ``allowSubsectionPrefix`` — used by the Compare universal-section picker, whose
 *  resolved primary number may be a parent absent from a subsection-only TOC (EU
 *  4.4 → 4.4.1/4.4.2) — fall back to the first subsection. The ``c + '.'`` boundary
 *  mirrors ``expandSectionFamilies``: "4.4" pulls 4.4.1 but never 4.40 or 4.4A.
 *  Returns ``null`` when nothing matches. Pure/testable. */
export function findSectionTocEntry(
  tocEntries: TOCEntry[],
  sectionNumber: string,
  allowSubsectionPrefix: boolean,
): TOCEntry | null {
  const trimmed = sectionNumber.trim().replace(/\.$/, '');
  if (!trimmed) return null;
  const earliestByPage = (entries: TOCEntry[]): TOCEntry | null =>
    entries.length === 0
      ? null
      : [...entries].sort(
          (a, b) => (a.page_start ?? Infinity) - (b.page_start ?? Infinity),
        )[0] ?? null;
  const exact = earliestByPage(
    tocEntries.filter((e) => e.section_number === trimmed),
  );
  if (exact || !allowSubsectionPrefix) return exact;
  return earliestByPage(
    tocEntries.filter((e) => e.section_number?.startsWith(trimmed + '.')),
  );
}

/** Landing plan for the EU-historical citation path (US-SBA parity). Priority:
 *  (1) the section via the TOC; (2) the section's FIRST geometry box (page +
 *  y0); (3) the citation page + Y. Pure/testable — the effect just executes it.
 *  ``page`` is 1-based throughout and passed through unchanged. */
export type LandingPlan =
  | { kind: 'section' }
  | { kind: 'offset'; page: number; y: number }
  | { kind: 'none' };

export function planLanding(opts: {
  sectionMatched: boolean;
  firstSpan?: SectionSpan | null | undefined;
  landingPage?: number | undefined;
  landingY?: number | undefined;
}): LandingPlan {
  if (opts.sectionMatched) return { kind: 'section' };
  if (opts.firstSpan) {
    return { kind: 'offset', page: opts.firstSpan.page, y: opts.firstSpan.box[1] ?? 0 };
  }
  if (opts.landingPage && opts.landingPage >= 1) {
    return { kind: 'offset', page: opts.landingPage, y: opts.landingY ?? 0 };
  }
  return { kind: 'none' };
}

/**
 * Where a citation click should land, given the cited chunk and (optionally)
 * the resolver's page.
 *
 * Two rules, both load-bearing:
 *
 * 1. **The resolver's page wins when it disagrees with the chunk's page.**
 *    Until 2026-09-03 it was computed and then ignored: the viewer always
 *    navigated by `chunk.page_num`, so the resolver's entire navigation
 *    decision — 7.5% of resolutions, the original bug — was a no-op in the UI.
 *
 * 2. **When the page changes, the chunk's bbox must NOT be used to position
 *    it.** `chunk.bbox` describes the chunk's own page. Applying a page-12
 *    offset to page 11 scrolls to an arbitrary place and looks like a
 *    mis-highlight. There is nothing trustworthy to offset by in that case
 *    (the resolver returns no geometry), so we land at the top of the page and
 *    draw nothing. Landing on the right page with no rectangle is strictly
 *    better than today; it is not a placeholder for a rectangle.
 *
 * Returns `null` when there is nothing to scroll to yet — either no page at
 * all, or the page list has not loaded so `pdf_height_pt` is unknown and an
 * offset cannot be computed. The caller re-runs when `state.pages` arrives;
 * returning `{y: 0}` instead would scroll to the top of the page once and
 * never correct itself.
 */
export function planCitationScroll(opts: {
  chunkId?: number | null | undefined;
  chunkPage?: number | null | undefined;
  chunkBboxY0?: number | null | undefined;
  pdfHeightPt?: number | null | undefined;
}): { page: number; y: number } | null {
  const chunkPage = typeof opts.chunkPage === 'number' && opts.chunkPage >= 1
    ? opts.chunkPage
    : null;

  if (chunkPage === null) return null;
  const h = opts.pdfHeightPt ?? 0;
  if (!(h > 0)) return null;
  return {
    page: chunkPage,
    y: (opts.chunkBboxY0 ?? 0) / h,
  };
}

/**
 * Which PAGE-keyed highlights does this citation get, and where?
 *
 * The case it exists for: a progressive-read citation, which addresses its
 * evidence as (verified page, character range) and owns no chunk. The chunk path
 * cannot reach it — `chunkAnchors` is keyed by chunk_id — even though the box is
 * computed the same way in both cases, by matching the cited sentence against
 * that page's word boxes.
 *
 * Returns `[]` rather than a best effort. Every refusal below is a case where a
 * box would be drawn over text nobody verified:
 *
 *  - the span owns a chunk → the chunk path owns the highlight; drawing both
 *    puts two overlays on one sentence, which reads as two pieces of evidence
 *  - no page → nothing to draw on
 *  - page < 1 → not a real page; 1-based everywhere, so 0 is an unfilled default
 *    that would silently target the first page of the document
 *  - no sentence → nothing to locate
 *
 * ONE ENTRY PER CITED SPAN, and the refusals apply per span. This read only
 * `citation.page` / `anchor_start` / `anchor_end` — the FIRST span's scalars —
 * while `evidence_anchors` has carried `{chunk_id, page, start, end}` per span
 * since the multi-span fix. So evidence on two pages, or twice on one page, drew
 * a single box with nothing to say the rest existed. `qa.ts` records the
 * measurement: 5 of 12 spanned citations carry more than one span.
 *
 * The exclusion against the chunk path is therefore PER SPAN, which is what it
 * always meant. A mixed citation — some spans owning a chunk, some not — used to
 * lose its chunk-less spans to BOTH paths at once: `seedChunkAnchor` skips an
 * anchor with no chunk_id, and this function refused the whole citation because
 * its top-level chunk_id was set.
 *
 * Deliberately does NOT enforce a minimum anchor length: `computeAnchorBbox`
 * already refuses anything under 4 characters, and a second length rule in a
 * second file is a second thing to get out of step.
 */
export function planPageAnchors(citation: {
  page?: number | null | undefined;
  chunk_id?: number | null | undefined;
  anchor_start?: string | null | undefined;
  anchor_end?: string | null | undefined;
  /** The whole cited sentence, for the matcher's fifth pass. */
  evidence_text?: string | null | undefined;
  evidence_anchors?: ReadonlyArray<{
    chunk_id?: number | null | undefined;
    page?: number | null | undefined;
    start?: string | null | undefined;
    end?: string | null | undefined;
    text?: string | null | undefined;
  }> | undefined;
}): { page: number; anchor: ChunkAnchor }[] {
  // `text` IS CARRIED HERE TOO, and this path needed it most.
  //
  // These are the chunk-LESS citations — a progressive read owns no chunk by
  // construction, so this is the one path whose anchor is addressed by page
  // alone. It was also the one path left without the sentence: `plan` built
  // `{ start, end }`, so `ChunkAnchor.text` arrived undefined and the fifth
  // pass could never run for any progressive-read citation. Nothing errored and
  // no type complained — the field is optional — the box simply stayed missing
  // on exactly the OCR pages the pass was built to rescue.
  const plan = (
    page: number | null | undefined,
    chunkId: number | null | undefined,
    start: string | null | undefined,
    end: string | null | undefined,
    text: string | null | undefined,
  ): { page: number; anchor: ChunkAnchor } | null => {
    if (chunkId != null) return null;
    if (typeof page !== 'number' || !(page >= 1)) return null;
    if (!start || !start.trim()) return null;
    // `text` is OMITTED rather than nulled when absent, so an anchor without a
    // sentence stays byte-identical to what this returned before the fifth pass
    // existed — the same choice the `citeAnchors` URL writer makes.
    return {
      page,
      anchor: {
        start,
        end: end ?? null,
        ...(text ? { text: text.slice(0, ANCHOR_TEXT_MAX) } : {}),
      },
    };
  };

  if (citation.evidence_anchors?.length) {
    return citation.evidence_anchors
      .map((a) => plan(a.page, a.chunk_id, a.start, a.end, a.text))
      .filter((e): e is { page: number; anchor: ChunkAnchor } => e !== null);
  }

  // Older cached answers predate `evidence_anchors`; a replayed snapshot still
  // highlights its first span rather than losing the box entirely.
  const single = plan(
    citation.page, citation.chunk_id, citation.anchor_start, citation.anchor_end,
    citation.evidence_text,
  );
  return single ? [single] : [];
}

export interface RegulatoryViewerEmbedProps {
  docId: number;
  viewerApi: ViewerApi;
  highlightChunkIds?: number[];
  /**
   * Optional chunk_id → anchor mapping for sentence-level highlights.
   * Citations carry ``anchor_start`` (first ~30 chars of the quoted
   * sentence) and ``anchor_end`` (last ~30 chars).  Plumb both in and
   * the viewer will tighten the highlight to just the quoted lines.
   */
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /**
   * page_num → anchorS, for a citation that addresses its evidence by PAGE
   * rather than by chunk.
   *
   * A LIST per page, for the same reason `chunkAnchors` carries one: two cited
   * sentences can share a page, and `Record<number, ChunkAnchor>` let the second
   * overwrite the first before it was ever drawn.
   *
   * `chunkAnchors` above can only reach a sentence whose chunk was fetched into
   * `highlightChunkIds`. A progressive-read citation owns no chunk — it records
   * the verified page plus the sentence — so it needs this door instead. The
   * highlight itself is identical: both are computed by matching the sentence
   * against that page's own word boxes.
   *
   * Independent of `highlightChunkIds`: a page anchor draws whether or not any
   * chunk was loaded for its page.
   */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  /** Search term to box at word level inside the page text layer (Ctrl+F). */
  highlightTerm?: string | null;
  /** Several terms to box (any-of) — keyword Compare highlights every query row. */
  highlightTerms?: string[] | null;
  englishExact?: boolean;
  /** Per-term mark colors (parallel to highlightTerms) — keyword Compare colors
   *  each query row's matches in the PDF overlay. */
  highlightColors?: (string | undefined)[] | null;
  /** Deep-link landing (no chunk_id path, e.g. EU historical citations):
   *  preferred ``landingSection`` (via the TOC), plus a 1-based ``landingPage`` +
   *  normalized ``landingY`` (0..1) fallback. The scroll effect below depends on
   *  the loaded page + TOC lists, so it lands ONCE they mount (race-safe, no
   *  timer) at the section's offset — the same precision the chunk-citation path
   *  gives US SBA/EPAR. Other families omit these (no behavior change). */
  landingSection?: string | undefined;
  landingPage?: number | undefined;
  landingY?: number | undefined;
  /** Deterministic per-page section boxes (normalized 0..1) drawn as a highlight
   *  overlay — the reliable signal when there are no chunk_ids and text matching
   *  can't work (page text layer is one span per word). */
  sectionSpans?: SectionSpan[] | undefined;
  onChunkClick?: (chunk: ChunkMetadata) => void;
  /** JP PDF only: when provided, native-text pages get a block-hover
   *  translation overlay (Japanese page image stays; English on hover).
   *  Passed straight through to PageRenderer; other families omit it. */
  blockHoverTranslate?: (japaneseText: string, preserveHtml?: boolean, sectionId?: number | null) => Promise<string>;
  /** JP PDF only: hovered-block callback so the parent can render the
   *  translation in a side panel (paired with blockHoverTranslate). */
  onBlockHover?: (region: import('./PageRenderer').JpHoverRegion | null) => void;
  /** JP PDF only: forward the active in-document search phrase to the parent
   *  so it can (a) highlight the English term in the translation panel and
   *  (b) box the Japanese equivalent on the page. Null when the box clears. */
  onActiveSearchTermChange?: (term: string | null) => void;
  /** CJK-family only: the script regex that classifies a query as CJK (→
   *  client-side per-page CJK search) vs English (→ server translated search).
   *  Defaults to JP's kana+Han range so existing callers are byte-identical.
   *  zh_pdf passes a Han-only regex (no kana). */
  cjkScriptRe?: RegExp;
  /** CJK-family only: which box-OCR region field carries the source-language
   *  text used by the scanned-page search path. Defaults to ``text_ja`` (JP).
   *  zh_pdf passes ``text_zh``. */
  cjkRegionTextField?: 'text_ja' | 'text_zh';
  /** Bilingual in-document search mode (see ``DocSearchMode``). Only read
   *  when ``blockHoverTranslate`` is set. */
  docSearch?: DocSearchMode;
  /** docSearch='both' only: English search term → source-language term
   *  (``/<family>/translate-term``), searched in the page text too. */
  translateSearchTerm?: (en: string) => Promise<string>;
  /** Hide the left TOC sidebar (search + section tree). Defaults to shown.
   *  Set false for the NICE HTA demo docs, whose section tree is used for
   *  retrieval scoping but is deliberately not surfaced in the viewer. */
  showToc?: boolean;
  /** Compare view: render ONLY the pages covered by these section numbers
   *  (family-expanded against the TOC). Empty/undefined → every page. The
   *  section→page resolution lives here because the embed owns the true TOC. */
  visibleSectionNumbers?: string[] | undefined;
  /** Compare view: fires once the TOC loads so the parent can build a per-column
   *  section picker from the document's real sections. */
  onTocLoaded?: ((entries: TOCEntry[]) => void) | undefined;
}

// ─────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────

interface ViewerState {
  tocEntries: TOCEntry[];
  pages: PageInfo[];
  displayMode: 'normal' | 'source_only';
  displayNotice: string | null;
  loading: boolean;
  error: string | null;
}

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

const RegulatoryViewerEmbedInner = forwardRef<
  RegulatoryViewerHandle,
  RegulatoryViewerEmbedProps
>(function RegulatoryViewerEmbedInner(
  { docId, viewerApi, highlightChunkIds = [], chunkAnchors, pageAnchors, highlightTerm, highlightTerms, highlightColors, englishExact = false, landingSection, landingPage, landingY, sectionSpans, onChunkClick, blockHoverTranslate, onBlockHover, onActiveSearchTermChange, cjkScriptRe, cjkRegionTextField, docSearch = 'byScript', translateSearchTerm, showToc = true, visibleSectionNumbers, onTocLoaded },
  ref,
) {
  // Log-tag derived from the API prefix so each family's debug
  // output is distinguishable (`[/sba]`, `[/epar]`, `[/uk_pil]`).
  // Computed here so we don't have to re-pass a label prop.
  const logTag = `[viewer${viewerApi.prefix}]`;

  // Core viewer state
  const [state, setState] = useState<ViewerState>({
    tocEntries: [],
    pages: [],
    displayMode: 'normal',
    displayNotice: null,
    loading: true,
    error: null,
  });

  // Citation landing (see the chunk-metadata effect). Keyed on the id VALUES:
  // hosts build `highlightChunkIds` inline, so the array is new every render.
  const chunkIdsKey = highlightChunkIds.join(',');
  // `${docId}|${chunkIdsKey}` once that citation has landed — or once the
  // reader navigated (Contents / scrollToSection), which supersedes it.
  const landedChunkKeyRef = useRef<string | null>(null);
  // Same once-per-(doc, chunks) rule for the bilingual auto-select box: each
  // new nonce makes PageRenderer scroll the box to centre, so re-issuing it on
  // every re-run of the citation effect pulled the reader back to it.
  const autoSelectedKeyRef = useRef<string | null>(null);
  // Bumped on every section navigation, so a delayed citation re-scroll can
  // tell the reader has moved on since it was scheduled.
  const navSeqRef = useRef(0);
  // A scrollToSection call that arrived before the TOC loaded; replayed once
  // it does (see scrollToSection).
  const pendingSectionRef = useRef<{ sectionNumber: string; opts?: ScrollToSectionOpts } | null>(null);

  // Text spans cache: page_num -> TextSpan[]
  const [textSpansCache, setTextSpansCache] = useState<
    Map<number, TextSpan[]>
  >(new Map());

  // Ref mirror of textSpansCache so async handlers (scroll, chunk nav)
  // can read fresh values without waiting for React state propagation.
  const textSpansCacheRef = useRef<Map<number, TextSpan[]>>(new Map());

  // Chunk metadata keyed by chunk_id
  const [chunkMap, setChunkMap] = useState<Map<number, ChunkMetadata>>(
    new Map(),
  );

  // Active chunk (highlighted)
  const [activeChunkId, setActiveChunkId] = useState<number | null>(null);

  // The active in-document search phrase (from the sidebar box). When set,
  // it's boxed on every page like Ctrl+F (merged into the highlight overlay
  // alongside any compare/keyword terms). Null = no active search.
  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  // Clicked-search-result auto-select (bilingual jp_pdf / zh_pdf only). When a
  // result is clicked we not only scroll to its page but hand the page's hover
  // layer the term so it highlights + translates the matching block and scrolls
  // it into view — the user no longer hovers to find where the word is. The
  // nonce (a monotonic click counter) lets re-clicking the same result re-fire.
  const [autoSelect, setAutoSelect] = useState<AutoSelect | null>(null);
  const autoSelectNonce = useRef(0);
  // The ORIGINAL words the last Latin page-text search matched (accent /
  // translated-term hits), boxed on the page alongside the typed query.
  const matchedTermsRef = useRef<Set<string>>(new Set());
  const [matchedTerms, setMatchedTerms] = useState<string[]>([]);
  // docSearch='both': pages whose ORIGINAL text matched the last search, so a
  // click auto-selects the source block there and the English block elsewhere.
  const sourceHitPagesRef = useRef<Set<number>>(new Set());

  // Current page for TOC highlighting
  const [currentPage, setCurrentPage] = useState(1);

  // jp_pdf only: the section the user is currently HOVERING, resolved from
  // the hovered block's (page, y0). Overrides the page-based scroll-spy so
  // the TOC highlight is section-precise (two sections can share a page) and
  // tracks the same trigger as the English panel. Null = no active hover →
  // the TOC falls back to the page-based active section. Cleared on scroll so
  // pure scrolling reverts to the page baseline.
  const [hoverSectionId, setHoverSectionId] = useState<number | null>(null);

  // Zoom lightbox: index into ``state.pages`` of the page being zoomed
  // (null = closed). Click a page (or its magnifier) to open.
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);

  // Contents sidebar open/closed. Closed by default so the page image gets the
  // full width (matters in a compare panel); the rail button opens it.
  const [tocOpen, setTocOpen] = useState(false);

  // Section-jump feedback. ``headingFlash`` is the box of a POSITIVELY MATCHED
  // heading line — the page blinks it so the start of the section is findable
  // in a wall of PDF text. A miss just lands on the page and draws nothing: a
  // box on an unmatched line is exactly the wrong-line highlight that
  // line-level highlighting is withheld over.
  const [headingFlash, setHeadingFlash] =
    useState<{ page: number; box: [number, number, number, number]; key: number } | null>(null);
  const flashKeyRef = useRef(0);

  // Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // ⌘F scoping. The keydown handler is a global window listener, but the compare
  // view can mount several viewers at once. rootRef is this instance's outermost
  // element; pointerInsideRef tracks whether the pointer is currently over it.
  // Together they decide whether THIS viewer should claim ⌘F (see
  // shouldViewerHandleFind) instead of every mounted viewer fighting for focus.
  const rootRef = useRef<HTMLDivElement>(null);
  const pointerInsideRef = useRef(false);
  const pageRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  // #44 first-search race fix. The old design marked a page "loaded" in a Set
  // BEFORE its fetch resolved, so a search fired right after mount (while the
  // IntersectionObserver's own fetch was still in flight) saw the page as loaded,
  // returned early, and read stale/empty data — missing scanned-page matches.
  // Now: presence in the cache = loaded; a per-page in-flight promise = loading;
  // a caller AWAITS any in-flight fetch covering its range; and search reads
  // spans/regions from the cache REFS, never from a captured (stale) state.pages.
  const spanInFlight = useRef<Map<number, Promise<void>>>(new Map());
  const regionInFlight = useRef<Map<number, Promise<void>>>(new Map());
  const regionCacheRef = useRef<Map<number, Pick<PageInfo, 'line_regions' | 'table_regions'>>>(new Map());

  // ─────────────────────────────────────────────────────────────────
  // Scroll helper
  // ─────────────────────────────────────────────────────────────────

  /**
   * Resolve once the page's frame is mounted AND laid out.
   *
   * Citation scrolling used to fire on a fixed `setTimeout(..., 300)` guess.
   * If the frame wasn't in `pageRefsMap` yet the scroll silently did nothing
   * (see `scrollToPage`), and if it was mounted but its images hadn't sized
   * yet the box was still collapsed, so the scroll computed against a
   * zero-height page and landed somewhere else. Both are timing-dependent,
   * which is exactly why citation scroll worked "sometimes".
   *
   * `offsetHeight > 0` is the laid-out signal. Resolves with `null` on
   * timeout so callers can report instead of hanging.
   */
  const waitForPageElement = useCallback(
    (pageNum: number, timeoutMs = 8000): Promise<HTMLDivElement | null> =>
      new Promise((resolve) => {
        const started = performance.now();
        const tick = () => {
          const el = pageRefsMap.current.get(pageNum);
          if (el && el.offsetHeight > 0) {
            resolve(el);
            return;
          }
          if (performance.now() - started > timeoutMs) {
            resolve(null);
            return;
          }
          requestAnimationFrame(tick);
        };
        tick();
      }),
    [],
  );

  const scrollToPage = useCallback((pageNum: number) => {
    const el = pageRefsMap.current.get(pageNum);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    // Previously a silent no-op. A citation that resolves to a page the
    // viewer hasn't mounted is a real failure the user sees as "nothing
    // happened"; say so rather than swallowing it.
    console.warn(
      `${logTag} scrollToPage(${pageNum}): page frame not mounted; scroll skipped`,
    );
  }, [logTag]);

  // Scroll so a specific normalized Y on the given page lands ~12%
  // from the top of the viewport. Used for CEPI chunk bbox scroll
  // and heading-anchored section nav.
  const scrollToPageOffset = useCallback(
    (pageNum: number, normalizedY: number) => {
      const pageEl = pageRefsMap.current.get(pageNum);
      const container = scrollContainerRef.current;
      if (!pageEl || !container) {
        scrollToPage(pageNum);
        return;
      }
      const pageRect = pageEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const pageTopInContainer =
        container.scrollTop + (pageRect.top - containerRect.top);
      const topMargin = containerRect.height * 0.12;
      const targetY = Math.max(
        0,
        pageTopInContainer + pageRect.height * normalizedY - topMargin,
      );
      container.scrollTo({ top: targetY, behavior: 'smooth' });
    },
    [scrollToPage],
  );

  // CEPI citation → auto-select + translate the paragraph region the cited chunk
  // sits in (bilingual jp_pdf / zh_pdf only). Reuses the search-click autoSelect
  // path but matches by GEOMETRY (the chunk's normalized y-band), so the cited
  // content lands translated in the side panel + boxed as a clean region — no
  // hovering. The signal persists (nonce) until the page's layer consumes it, so
  // it works even though the layer mounts only after the scroll brings the page
  // into view. Monolingual families (no blockHoverTranslate) are unaffected.
  const fireCitationAutoSelect = useCallback(
    (chunk: ChunkMetadata) => {
      if (!blockHoverTranslate || !chunk.page_num) return;
      const page = state.pages.find((p) => p.page_num === chunk.page_num);
      const pdfH = page?.pdf_height_pt ?? 0;
      const rawY0 = chunk.bbox?.y0 ?? 0;
      const rawY1 = chunk.bbox?.y1 ?? rawY0;
      // Same normalization the scroll path uses (chunk bbox is in PDF points).
      const y0n = pdfH > 0 ? rawY0 / pdfH : rawY0;
      const y1n = pdfH > 0 ? rawY1 / pdfH : rawY1;
      autoSelectNonce.current += 1;
      setAutoSelect({
        page: chunk.page_num,
        bbox: [0, y0n, 1, y1n],
        nonce: autoSelectNonce.current,
      });
    },
    [blockHoverTranslate, state.pages],
  );

  // ─────────────────────────────────────────────────────────────────
  // Text span loading (defined before nav helpers that use it)
  // ─────────────────────────────────────────────────────────────────

  const loadTextSpansForRange = useCallback(
    async (
      fromPage: number,
      toPage: number,
    ): Promise<Map<number, TextSpan[]>> => {
      // Pages that are neither cached nor already being fetched.
      const missing: number[] = [];
      for (let p = fromPage; p <= toPage; p++) {
        if (!textSpansCacheRef.current.has(p) && !spanInFlight.current.has(p)) {
          missing.push(p);
        }
      }
      if (missing.length > 0) {
        let req!: Promise<void>;
        req = (async () => {
          try {
            const resp = await viewerApi.fetchBatchTextSpans(docId, fromPage, toPage);
            const updated = new Map(textSpansCacheRef.current);
            for (const [pageStr, data] of Object.entries(resp.pages)) {
              updated.set(Number(pageStr), data.text_spans);
            }
            textSpansCacheRef.current = updated;
            setTextSpansCache(updated);
          } catch (err) {
            console.error(`${logTag} Failed to load text spans:`, err);
          } finally {
            for (const p of missing) {
              if (spanInFlight.current.get(p) === req) spanInFlight.current.delete(p);
            }
          }
        })();
        for (const p of missing) spanInFlight.current.set(p, req);
      }
      // Await every in-flight fetch covering this range (including one started by
      // a concurrent caller / the observer) so we never read before data lands.
      const pending = new Set<Promise<void>>();
      for (let p = fromPage; p <= toPage; p++) {
        const req = spanInFlight.current.get(p);
        if (req) pending.add(req);
      }
      await Promise.all(pending);
      // Read the now-populated cache ref — never stale.
      const result = new Map<number, TextSpan[]>();
      for (let p = fromPage; p <= toPage; p++) {
        const spans = textSpansCacheRef.current.get(p);
        if (spans) result.set(p, spans);
      }
      return result;
    },
    [docId, viewerApi, logTag],
  );

  // Lazy-load line/table regions for a page range and MERGE them into the
  // corresponding entries of ``state.pages`` (regions ship separately from
  // /pages now, arriving null and filled in on scroll). Mirrors
  // loadTextSpansForRange; PageRenderer picks the merged regions up on the
  // next render with no PageRenderer change.
  const loadRegionsForRange = useCallback(
    async (fromPage: number, toPage: number): Promise<void> => {
      const missing: number[] = [];
      for (let p = fromPage; p <= toPage; p++) {
        if (!regionCacheRef.current.has(p) && !regionInFlight.current.has(p)) {
          missing.push(p);
        }
      }
      if (missing.length > 0) {
        let req!: Promise<void>;
        req = (async () => {
          try {
            const resp = await viewerApi.fetchBatchRegions(docId, fromPage, toPage);
            // Cache ref FIRST (search reads from here), then merge into
            // state.pages so PageRenderer picks the regions up on next render.
            const updated = new Map(regionCacheRef.current);
            for (let p = fromPage; p <= toPage; p++) {
              const data = resp.pages[String(p)];
              if (data) updated.set(p, {
                line_regions: data.line_regions,
                table_regions: data.table_regions,
              });
            }
            regionCacheRef.current = updated;
            setState((prev) => {
              if (prev.pages.length === 0) return prev;
              let changed = false;
              const pages = prev.pages.map((page) => {
                const data = resp.pages[String(page.page_num)];
                if (!data) return page;
                changed = true;
                return {
                  ...page,
                  line_regions: data.line_regions,
                  table_regions: data.table_regions,
                };
              });
              return changed ? { ...prev, pages } : prev;
            });
          } catch (err) {
            console.error(`${logTag} Failed to load regions:`, err);
          } finally {
            for (const p of missing) {
              if (regionInFlight.current.get(p) === req) regionInFlight.current.delete(p);
            }
          }
        })();
        for (const p of missing) regionInFlight.current.set(p, req);
      }
      const pending = new Set<Promise<void>>();
      for (let p = fromPage; p <= toPage; p++) {
        const req = regionInFlight.current.get(p);
        if (req) pending.add(req);
      }
      await Promise.all(pending);
    },
    [docId, viewerApi, logTag],
  );

  // Scroll to a TOC entry's heading by searching the page's text-span
  // layer for "{shortNumber} {title}". Falls back to page-top on miss.
  const scrollToTOCEntry = useCallback(
    async (entry: TOCEntry) => {
      if (!entry.page_start) return;
      const page = entry.page_start;
      // The reader chose where to be: a pending/late citation landing must
      // not pull them back to the cited page.
      landedChunkKeyRef.current = `${docId}|${chunkIdsKey}`;
      navSeqRef.current += 1;

      const spanMap = await loadTextSpansForRange(page, page);
      const spans = spanMap.get(page) ?? [];
      // Bilingual docs: the page shows the SOURCE heading, the TOC title is English.
      const sourceTitle = entry.title_src ?? entry.title_zh ?? entry.title_ja;
      const line =
        (sourceTitle ? findHeadingLine(spans, entry.section_number, sourceTitle) : null) ??
        findHeadingLine(spans, entry.section_number, entry.title);

      if (line !== null) {
        // Spans are usually a 0–1 fraction, but the gemini_ocr path persists
        // PDF points; scrollToPageOffset only accepts a fraction.
        // normalizeSpanY is dimension-agnostic (>1 ⇒ divide by the extent), so
        // the width normalizes the x edges the same way.
        const pg = state.pages.find((p) => p.page_num === page);
        const pdfH = pg?.pdf_height_pt ?? 0;
        const pdfW = pg?.pdf_width_pt ?? 0;
        scrollToPageOffset(page, normalizeSpanY(line.y0, pdfH));
        flashKeyRef.current += 1;
        setHeadingFlash({
          page,
          box: [
            normalizeSpanY(line.x0, pdfW),
            normalizeSpanY(line.y0, pdfH),
            normalizeSpanY(line.x1, pdfW),
            normalizeSpanY(line.y1, pdfH),
          ],
          key: flashKeyRef.current,
        });
      } else {
        // Miss: land on the section's page, draw nothing.
        scrollToPage(page);
        setHeadingFlash(null);
      }
    },
    [loadTextSpansForRange, scrollToPage, scrollToPageOffset, state.pages, docId, chunkIdsKey],
  );

  // Scroll to a section by number using the current document's TOC. Returns
  // false when the number isn't in the TOC (caller falls back). Shared by the
  // imperative handle AND the deep-link landing effect below.
  const scrollToSection = useCallback((
    sectionNumber: string,
    opts?: ScrollToSectionOpts,
  ): boolean => {
    // Exact match only by default.  Bundle-wide fuzzy resolution (prefix omission,
    // trailing period, case variation, cross-doc disambiguation) lives in the
    // Phase 10b backend resolver the caller falls back to when we return ``false``.
    // ``allowSubsectionPrefix`` opts a caller (the Compare universal-section picker)
    // into a client-side parent→first-subsection fallback; other callers keep the
    // exact-only contract so their ``false``-triggered resolvers still fire.
    if (state.loading && opts?.deferUntilLoaded) {
      // The TOC isn't here yet, so "not found" would be a lie (it showed a
      // false "§X is not in this document's contents" banner while a PDF
      // loaded). Accept the request and replay it once the TOC arrives.
      pendingSectionRef.current = { sectionNumber, ...(opts ? { opts } : {}) };
      return true;
    }
    pendingSectionRef.current = null;
    const earliest = findSectionTocEntry(
      state.tocEntries, sectionNumber, opts?.allowSubsectionPrefix ?? false,
    );
    if (!earliest) {
      console.debug(
        `${logTag} section_number not found in current doc TOC:`,
        sectionNumber,
      );
      setHeadingFlash(null);
      return false;
    }
    if (earliest.page_start) {
      // Heading-anchor scroll runs async (awaits text-span fetch). Boolean
      // return is synchronous so callers can decide whether to invoke the
      // Phase 10b fallback resolver.
      void scrollToTOCEntry(earliest);
      return true;
    }
    return false;
  }, [state.loading, state.tocEntries, scrollToTOCEntry, logTag]);

  // Replay a scrollToSection that arrived while the document was loading.
  useEffect(() => {
    if (state.loading || !pendingSectionRef.current) return;
    const { sectionNumber, opts } = pendingSectionRef.current;
    pendingSectionRef.current = null;
    scrollToSection(sectionNumber, opts);
  }, [state.loading, scrollToSection]);

  // Deep-link landing (EU-historical citation path — no chunk_id). Race-safe:
  // depends on the loaded page list, so it fires ONCE pages + TOC mount, not on
  // a timer that can beat a large PDF's page render. Preference order mirrors US
  // SBA precision: (1) scrollToSection via the TOC; (2) the section's first
  // geometry box (page + y0); (3) the citation page + Y. Lands once per target.
  const landedRef = useRef<string | null>(null);
  useEffect(() => {
    if (highlightChunkIds.length > 0) return;   // chunk path owns the scroll
    if (state.pages.length === 0) return;        // wait for pages (+ TOC) to load
    const firstSpan = sectionSpans && sectionSpans.length ? sectionSpans[0] : null;
    const key = `${docId}|${landingSection ?? ''}|${landingPage ?? ''}|${firstSpan?.page ?? ''}`;
    if (!landingSection && !landingPage && !firstSpan) return;
    if (landedRef.current === key) return;
    landedRef.current = key;
    const matched = !!landingSection && sectionInToc(state.tocEntries, landingSection);
    const plan = planLanding({ sectionMatched: matched, firstSpan, landingPage, landingY });
    // Give the just-mounted pages a tick to lay out before measuring offsets.
    const t = setTimeout(() => {
      if (plan.kind === 'section') scrollToSection(landingSection!);
      else if (plan.kind === 'offset') scrollToPageOffset(plan.page, plan.y);
    }, 60);
    return () => clearTimeout(t);
  }, [docId, landingSection, landingPage, landingY, sectionSpans, highlightChunkIds.length,
      state.pages, state.tocEntries, scrollToSection, scrollToPageOffset]);

  // ─────────────────────────────────────────────────────────────────
  // Expose handle to parent
  // ─────────────────────────────────────────────────────────────────

  useImperativeHandle(ref, () => ({
    scrollToPage,
    highlightChunks: async (chunkIds: number[]) => {
      if (chunkIds.length === 0) return;
      try {
        const resp = await viewerApi.fetchChunksBatch(docId, chunkIds);
        const map = new Map<number, ChunkMetadata>();
        for (const chunk of resp.chunks) {
          map.set(chunk.chunk_id, chunk);
        }
        setChunkMap((prev) => {
          const next = new Map(prev);
          for (const [k, v] of map) next.set(k, v);
          return next;
        });

        // Scroll to first chunk using its bbox Y so the highlight
        // lands in the viewport, not at the page top.
        // bbox.y0 from the API is in PDF points; scrollToPageOffset
        // expects a 0–1 fraction of page height.
        const firstId = chunkIds[0] ?? null;
        const firstChunk = firstId !== null ? map.get(firstId) : undefined;
        if (firstChunk?.page_num) {
          const page = state.pages.find(
            (p) => p.page_num === firstChunk.page_num,
          );
          const plan = planCitationScroll({
            chunkId: firstChunk.chunk_id,
            chunkPage: firstChunk.page_num,
            chunkBboxY0: firstChunk.bbox?.y0,
            pdfHeightPt: page?.pdf_height_pt,
          });
          if (plan) {
            setTimeout(() => scrollToPageOffset(plan.page, plan.y), 300);
          }
          fireCitationAutoSelect(firstChunk);
        }
        setActiveChunkId(firstId);
      } catch (err) {
        console.error(`${logTag} Failed to load chunk metadata:`, err);
      }
    },
    scrollToSection,
  }), [docId, viewerApi, logTag, scrollToPage, scrollToPageOffset, scrollToSection,
       state.pages, fireCitationAutoSelect]);

  // ─────────────────────────────────────────────────────────────────
  // Initial data fetch: TOC + Pages
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!docId || isNaN(docId)) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Invalid document ID',
      }));
      return;
    }

    let cancelled = false;

    // Reset state on docId change
    setState({
      tocEntries: [],
      pages: [],
      displayMode: 'normal',
      displayNotice: null,
      loading: true,
      error: null,
    });
    setTextSpansCache(new Map());
    textSpansCacheRef.current = new Map();
    setChunkMap(new Map());
    setActiveChunkId(null);
    setSearchTerm(null);
    setAutoSelect(null);
    setZoomIdx(null);
    setHoverSectionId(null);
    spanInFlight.current.clear();
    regionInFlight.current.clear();
    regionCacheRef.current = new Map();

    async function load() {
      try {
        const [tocResp, pagesResp] = await Promise.all([
          viewerApi.fetchTOC(docId),
          viewerApi.fetchPages(docId),
        ]);

        if (cancelled) return;

        setState({
          tocEntries: tocResp.sections,
          pages: pagesResp.pages,
          displayMode:
            viewerApi.prefix === '/sba'
              ? pagesResp.display_mode ?? 'normal'
              : 'normal',
          displayNotice:
            viewerApi.prefix === '/sba'
              ? pagesResp.display_notice ?? null
              : null,
          loading: false,
          error: null,
        });
        // Lift the real TOC so a Compare column can build a per-document
        // section picker (the drug's own `sections` are wrong/empty for a
        // non-US PDF). Best-effort; never blocks the load.
        onTocLoaded?.(tocResp.sections);
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load document',
        }));
      }
    }

    load();
    return () => { cancelled = true; };
  }, [docId, viewerApi]);

  // ─────────────────────────────────────────────────────────────────
  // Fetch chunk metadata when highlightChunkIds prop changes
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!chunkIdsKey || !docId) return;
    const ids = chunkIdsKey.split(',').map(Number);
    const landingKey = `${docId}|${chunkIdsKey}`;

    let cancelled = false;
    // The citation scroll lands ONCE per (doc, chunks). This effect re-runs on
    // every `state.pages` change (the lazy line/table-region fetch merges into
    // it) — re-landing then yanked the reader back to the cited page after
    // they, or a section jump, had moved on (Compare: any re-render did it).
    const land = (fn: () => void) => {
      if (cancelled) return;
      if (landedChunkKeyRef.current === landingKey) return;
      landedChunkKeyRef.current = landingKey;
      fn();
    };

    async function loadChunks() {
      try {
        const resp = await viewerApi.fetchChunksBatch(docId, ids);
        if (cancelled) return;

        const map = new Map<number, ChunkMetadata>();
        for (const chunk of resp.chunks) {
          map.set(chunk.chunk_id, chunk);
        }
        setChunkMap(map);

        if (ids.length > 0) {
          const firstHighlightId = ids[0] ?? null;
          setActiveChunkId(firstHighlightId);
          const firstChunk = firstHighlightId !== null ? map.get(firstHighlightId) : undefined;
          if (!firstChunk?.page_num) {
            // No page on the cited chunk: nothing can be scrolled to. Report
            // it — this is a data gap (chunks ingested without page_num), not
            // a user error, and silence here reads as "the link is broken".
            if (firstChunk) {
              console.warn(
                `${logTag} chunk ${firstHighlightId} has no page_num; `
                + 'cannot scroll to citation',
              );
            }
          } else {
            const page = state.pages.find(
              (p) => p.page_num === firstChunk.page_num,
            );
            // Returns null while the page list is still loading, because
            // pdfH=0 previously yielded normalizedY=0 and scrolled to the TOP
            // of the page instead of the cited line. The effect re-runs when
            // `state.pages` arrives.
            const plan = planCitationScroll({
              chunkId: firstChunk.chunk_id,
              chunkPage: firstChunk.page_num,
              chunkBboxY0: firstChunk.bbox?.y0,
              pdfHeightPt: page?.pdf_height_pt,
            });
            if (!plan) return;

            // Scroll once the page frame is actually mounted and laid out,
            // instead of guessing 300ms and silently missing.
            void waitForPageElement(plan.page).then((el) => {
              if (!el) {
                // NOT a dead end. Every page is mounted (``state.pages.map``),
                // but a frame far down the list has no height until its lazy
                // image loads — and that image loads when it nears the
                // viewport. Waiting for height before scrolling is therefore
                // circular: it cannot scroll there until it has height, and it
                // has no height until scrolled near.
                //
                // Abandoning left the reader on an unrelated page with nothing
                // to show the citation had done anything — measured on staging
                // as "page 101/104/126 did not mount in time; citation scroll
                // skipped" on a 147-page review whose data was perfectly
                // healthy. A coarse scroll breaks the cycle; the image then
                // loads, the frame gains height, and the precise offset path
                // takes over on the next citation.
                land(() => {
                  console.warn(
                    `${logTag} page ${plan.page} not laid out yet; `
                    + 'scrolled to the page without a line offset',
                  );
                  scrollToPage(plan.page);
                  // The coarse scroll is what breaks the cycle: the frame is now
                  // near the viewport, so its image loads and it gains height.
                  // Take the precise line offset on that second pass rather than
                  // leaving the reader at the top of a 1-page-of-147 frame —
                  // unless the reader navigated elsewhere in the meantime.
                  const seq = navSeqRef.current;
                  void waitForPageElement(plan.page, 4000).then((laid) => {
                    if (!laid || navSeqRef.current !== seq) return;
                    scrollToPageOffset(plan.page, plan.y);
                  });
                });
                return;
              }
              land(() => scrollToPageOffset(plan.page, plan.y));
            });
            if (autoSelectedKeyRef.current !== landingKey) {
              autoSelectedKeyRef.current = landingKey;
              fireCitationAutoSelect(firstChunk);
            }
          }
        }
      } catch (err) {
        console.error(`${logTag} Failed to load chunk metadata:`, err);
      }
    }

    loadChunks();
    return () => { cancelled = true; };
  }, [chunkIdsKey, docId, viewerApi, logTag,
      scrollToPageOffset, scrollToPage, state.pages, waitForPageElement, fireCitationAutoSelect]);

  // ─────────────────────────────────────────────────────────────────
  // Lazy-load text spans via IntersectionObserver
  // ─────────────────────────────────────────────────────────────────

  // Mirror ``currentPage`` into a ref so the observer callback can
  // read the latest value without re-subscribing on every scroll.
  const currentPageRef = useRef(currentPage);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    if (state.pages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let topVisiblePage = currentPageRef.current;
        let topVisibleY = Infinity;

        for (const entry of entries) {
          const pageNum = Number(
            (entry.target as HTMLElement).dataset['page'],
          );
          if (isNaN(pageNum)) continue;

          if (entry.isIntersecting) {
            const rect = entry.boundingClientRect;
            if (rect.top < topVisibleY) {
              topVisibleY = rect.top;
              topVisiblePage = pageNum;
            }

            loadTextSpansForRange(
              Math.max(1, pageNum - 1),
              Math.min(state.pages.length, pageNum + 1),
            );
            loadRegionsForRange(
              Math.max(1, pageNum - 1),
              Math.min(state.pages.length, pageNum + 1),
            );
          }
        }

        if (topVisiblePage !== currentPageRef.current) {
          setCurrentPage(topVisiblePage);
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px 0px',
        threshold: 0.1,
      },
    );

    for (const [, el] of pageRefsMap.current) {
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [state.pages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────
  // Eager-load the FULL text layer for native browser Find (⌘F / Ctrl+F)
  // ─────────────────────────────────────────────────────────────────
  // The observer above injects text spans only for pages near the
  // viewport, so the browser's own Find bar can only match text on pages
  // you've already scrolled near — text deeper in the doc isn't in the
  // DOM yet, so ⌘F reports no match (the reported bug). Pull every page's
  // (transparent) text layer up front so ⌘F can find anything in the doc.
  // The batch endpoint caps at 50 pages/request, so walk it in 50-page
  // windows; loadTextSpansForRange's cache/in-flight guard dedupes against
  // the observer's own fetches.
  useEffect(() => {
    const total = state.pages.length;
    if (total === 0) return;
    let cancelled = false;
    (async () => {
      for (let start = 1; start <= total && !cancelled; start += 50) {
        await loadTextSpansForRange(start, Math.min(total, start + 49));
      }
    })();
    return () => { cancelled = true; };
  }, [state.pages.length, loadTextSpansForRange]);

  // ─────────────────────────────────────────────────────────────────
  // Eager-load the FULL region layer (JP hover highlights)
  // ─────────────────────────────────────────────────────────────────
  // Regions ship separately from /pages now (slim initial payload). The
  // IntersectionObserver loads them for the visible range on scroll, but it
  // has a ref-timing race on mount: page 1 is already in the viewport, so if
  // its ref isn't attached when the observer subscribes, page 1's regions
  // never load and the hover HIGHLIGHT (RegionHoverLayer, gated on
  // page.line_regions.length) silently disappears until you scroll. Text
  // spans survive the same race via their own eager loader above; regions
  // had no backstop. Mirror it: pull every page's regions in the background
  // (50-page windows, post-paint so the initial render stays fast). The
  // cache/in-flight guard dedupes against the observer's own fetches.
  useEffect(() => {
    const total = state.pages.length;
    if (total === 0) return;
    let cancelled = false;
    (async () => {
      for (let start = 1; start <= total && !cancelled; start += 50) {
        await loadRegionsForRange(start, Math.min(total, start + 49));
      }
    })();
    return () => { cancelled = true; };
  }, [state.pages.length, loadRegionsForRange]);

  // ─────────────────────────────────────────────────────────────────
  // ⌘F / Ctrl+F → focus the in-document search box (on virtualized docs)
  // ─────────────────────────────────────────────────────────────────
  // Overlay virtualization keeps only near-viewport pages' text in the DOM,
  // so the browser's native Find can't see the whole document. The sidebar
  // "Search in document" box is a whole-document search replacement — it
  // searches the full doc (via the JS text cache / server search) and jumps to
  // the hit (it lacks native Find's next/prev nav + live match count, so it's
  // not a strict superset, but it's the only thing that can see the off-DOM
  // pages). Intercept the keystroke and focus it so the muscle memory keeps
  // working. Only on large docs; small docs render fully, so native ⌘F still
  // works there and we leave it alone.
  //
  // SCOPING: the listener is on window and the compare view can mount several
  // viewers at once. We only claim ⌘F when the user is actually in THIS viewer
  // (pointer over it, or focus already inside it) — otherwise every mounted
  // instance would fire and the last-registered one would steal focus into the
  // wrong pane. When the user is outside every viewer, none intercept and
  // native Find is left untouched.
  useEffect(() => {
    if (state.pages.length <= OVERLAY_VIRT_THRESHOLD) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F')) {
        const active =
          pointerInsideRef.current ||
          (rootRef.current?.contains(document.activeElement) ?? false);
        if (!shouldViewerHandleFind(state.pages.length, active)) return;
        const input = searchInputRef.current;
        if (input) {
          e.preventDefault();
          input.focus();
          input.select();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state.pages.length]);

  // ─────────────────────────────────────────────────────────────────
  // Client-side in-document search (replaces the server FTS)
  // ─────────────────────────────────────────────────────────────────
  // The sidebar "Search in document…" box used to call the backend
  // websearch_to_tsquery FTS (stemmed, whole-doc, ranked). We now search
  // the SAME text layer the page renders — i.e. exactly what the browser's
  // Ctrl+F would see in the DOM — entirely client-side: exact, case-
  // insensitive substring match, no stemming, no server round-trip.
  // Each line/span is matched on its own (a match must be contiguous
  // within a line), mirroring how Ctrl+F behaves on our per-line text
  // overlay. Returns the same SearchResponse shape so DocumentSearch's
  // p.N + excerpt list is unchanged.
  const localSearchDocument = useCallback(
    async (_docId: number, query: string, limit?: number): Promise<SearchResponse> => {
      const needle = foldLatin(query.trim());
      const total = state.pages.length;
      const empty: SearchResponse = { document_id: docId, query, total_results: 0, results: [] };
      if (!needle || total === 0) return empty;

      // Ensure every page's text layer is loaded before searching (the
      // eager-load effect may still be in flight, or a window may have
      // failed). 50-page windows; cache/in-flight guard dedupes.
      for (let start = 1; start <= total; start += 50) {
        await loadTextSpansForRange(start, Math.min(total, start + 49));
      }

      const results = [];
      for (const page of state.pages) {
        // No default cap: this is a client-side scan of ONE document's
        // already-loaded text layer, so the result set is bounded by the
        // page count (≤ one row per page). A cap (was 50) silently hid
        // later pages that contain the term — the user could see a term on
        // a page past the cap but it never appeared in the results list.
        // ``limit`` is honoured only if a caller explicitly passes one.
        if (limit != null && results.length >= limit) break;
        const spans = textSpansCacheRef.current.get(page.page_num);
        if (!spans || spans.length === 0) continue;
        // First line on this page that contains the phrase → page-level hit
        // (one row per page, matching the previous FTS panel behaviour).
        // Accent-insensitive (foldLatin): "eingeschrankt" finds "eingeschränkt".
        const hit = spans.find((s) => foldLatin(s.text).includes(needle));
        if (hit) {
          const at = foldLatin(hit.text).indexOf(needle);
          matchedTermsRef.current.add(hit.text.slice(at, at + needle.length));
          results.push({
            page_num: page.page_num,
            rank: 1, // client search has no relevance score; preserve page order
            headline: buildHeadline(hit.text, needle, foldLatin),
          });
        }
      }

      return { document_id: docId, query, total_results: results.length, results };
    },
    [docId, state.pages, loadTextSpansForRange],
  );

  // Japanese in-document search (jp_pdf only). Searches the page's OWN Japanese
  // text — native pages via their real text_spans, SCANNED pages via the
  // box-OCR line_regions' text_ja (the only reliable Japanese there, since
  // Tesseract text_spans are garbage). A direct substring match: no
  // translation, so counts are honest and it works uniformly on both. Pairs
  // with the page-side RegionSearchHighlight / word highlight.
  const localSearchDocumentJa = useCallback(
    async (_docId: number, query: string, limit?: number): Promise<SearchResponse> => {
      const needle = query.trim();
      const total = state.pages.length;
      const empty: SearchResponse = { document_id: docId, query, total_results: 0, results: [] };
      if (!needle || total === 0) return empty;

      // Ensure BOTH layers are loaded (spans for native pages, regions for
      // scanned). 50-page windows; the loaded-page guards dedupe.
      for (let start = 1; start <= total; start += 50) {
        await loadTextSpansForRange(start, Math.min(total, start + 49));
        await loadRegionsForRange(start, Math.min(total, start + 49));
      }

      const results = [];
      for (const page of state.pages) {
        if (limit != null && results.length >= limit) break;
        const spans = textSpansCacheRef.current.get(page.page_num) ?? [];
        const hasRealSpans = spans.some((s) => (s.block ?? -1) >= 0);
        // Native page → concatenate real spans; scanned page → concatenate the
        // box-OCR regions' Japanese. Concatenate (not per-span) so a term that
        // straddles two spans/regions still matches.
        const pageText = hasRealSpans
          ? spans.map((s) => s.text).join('')
          : (regionCacheRef.current.get(page.page_num)?.line_regions ?? [])
              .map((r) => (r as any)[cjkRegionTextField ?? 'text_ja'] || '').join('\n');
        if (pageText.includes(needle)) {
          results.push({
            page_num: page.page_num,
            rank: 1,
            headline: buildHeadline(pageText, needle),
          });
        }
      }

      return { document_id: docId, query, total_results: results.length, results };
    },
    [docId, state.pages, loadTextSpansForRange, loadRegionsForRange, cjkRegionTextField],
  );

  // jp_pdf search router: a Japanese query searches the page's own Japanese
  // text (client-side, above); an English query keeps the server search over
  // the translated chunk_text. Auto-detected by script — same rule as the
  // page-side highlight in JPPdfViewerEmbed, so the results list and the
  // highlighted surface always agree.
  const jpSearchDocument = useCallback(
    async (id: number, query: string, limit?: number): Promise<SearchResponse> => {
      if (docSearch === 'byScript') {
        return (cjkScriptRe ?? /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/).test(query)
          ? localSearchDocumentJa(id, query, limit)
          : viewerApi.searchDocument(id, query, limit);
      }
      // Latin source: search the original page text AND the English, one row
      // per page, labelled with the language that matched.
      // An English query on an untranslated label (DE/IT; native-only, no
      // stored English) would find nothing, so its source-language term
      // (/translate-term, cached) is searched in the page text as well.
      matchedTermsRef.current = new Set();
      const [src, en, term] = await Promise.all([
        localSearchDocument(id, query, limit),
        viewerApi.searchDocument(id, query, limit).catch(() => null),
        translateSearchTerm ? translateSearchTerm(query).catch(() => '') : Promise.resolve(''),
      ]);
      const srcTerm = (term || '').trim();
      const viaTerm = srcTerm && foldLatin(srcTerm) !== foldLatin(query.trim())
        ? (await localSearchDocument(id, srcTerm, limit)).results : [];
      const seen = new Set(src.results.map((r) => r.page_num));
      const srcAll = [...src.results, ...viaTerm.filter((r) => !seen.has(r.page_num))]
        .sort((a, b) => a.page_num - b.page_num);
      sourceHitPagesRef.current = new Set(srcAll.map((r) => r.page_num));
      setMatchedTerms([...matchedTermsRef.current]);
      return mergeBilingualSearch(id, query, srcAll, en?.results ?? []);
    },
    [localSearchDocumentJa, localSearchDocument, viewerApi, cjkScriptRe, docSearch, translateSearchTerm],
  );

  // ─────────────────────────────────────────────────────────────────
  // Navigation handlers
  // ─────────────────────────────────────────────────────────────────

  const handleTOCNavigate = useCallback(
    (entry: TOCEntry) => {
      void scrollToTOCEntry(entry);
    },
    [scrollToTOCEntry],
  );

  const handleSearchNavigate = useCallback(
    (pageNum: number, query?: string) => {
      scrollToPage(pageNum);
      // Auto-select is a bilingual-viewer feature: only jp_pdf / zh_pdf carry a
      // hover layer (signalled by blockHoverTranslate). Monolingual families
      // keep the plain scroll — the extra ``query`` arg is simply ignored.
      if (!blockHoverTranslate) return;
      const q = (query ?? searchTerm ?? '').trim();
      if (!q) return;
      // Same script rule the search router uses: a CJK query matches the block's
      // source text 1:1 ('src'); an English query matches the block's live
      // English translation ('en'). Keeps the highlighted block consistent with
      // which surface the search itself queried.
      const isSrc = docSearch === 'both'
        ? sourceHitPagesRef.current.has(pageNum)
        : (cjkScriptRe ?? /[぀-ゟ゠-ヿ一-鿿]/).test(q);
      autoSelectNonce.current += 1;
      setAutoSelect({ page: pageNum, term: q, mode: isSrc ? 'src' : 'en', nonce: autoSelectNonce.current });
    },
    [scrollToPage, blockHoverTranslate, cjkScriptRe, docSearch, searchTerm],
  );

  // Cleared by the hover layer once it has activated the auto-selected block,
  // so a virtualized page's layer can't re-fire the same select on remount.
  const clearAutoSelect = useCallback(() => setAutoSelect(null), []);

  const handleChunkClick = useCallback((chunk: ChunkMetadata) => {
    setActiveChunkId(chunk.chunk_id);
    onChunkClick?.(chunk);
  }, [onChunkClick]);

  // Open the zoom lightbox for the page with this page_num (resolve to its
  // index in state.pages, which is what the lightbox Prev/Next steps over).
  const handleZoomClick = useCallback((pageNum: number) => {
    const idx = state.pages.findIndex((p) => p.page_num === pageNum);
    if (idx >= 0) setZoomIdx(idx);
  }, [state.pages]);

  // Ensure the zoomed page's text spans (which carry the keyword <mark>
  // highlights) are loaded — inline pages lazy-load them via the observer, but
  // the lightbox can open / Prev-Next onto a page whose spans never scrolled
  // into view. Load the page and its neighbours so highlights are present.
  useEffect(() => {
    if (zoomIdx === null || state.pages.length === 0) return;
    const zp = state.pages[zoomIdx];
    if (!zp) return;
    void loadTextSpansForRange(
      Math.max(1, zp.page_num - 1),
      Math.min(state.pages.length, zp.page_num + 1),
    );
  }, [zoomIdx, state.pages, loadTextSpansForRange]);

  // Compare view "show only this section's pages": when visibleSectionNumbers is
  // set, render just the pages those sections cover (resolved against the real
  // TOC). An unresolvable section (not in THIS document) yields an empty set, so
  // we fall back to the whole document rather than blank the column. The zoom
  // lightbox still indexes the full `state.pages` and keys off page_num, so
  // filtering the rendered list never mis-opens it.
  const { visiblePages, filterMissed } = useMemo(() => {
    if (!visibleSectionNumbers || visibleSectionNumbers.length === 0) {
      return { visiblePages: state.pages, filterMissed: false };
    }
    const keep = pageNumsForSections(state.tocEntries, state.pages, visibleSectionNumbers);
    if (keep.size) {
      return { visiblePages: state.pages.filter((p) => keep.has(p.page_num)), filterMissed: false };
    }
    // Only a miss once the TOC + pages have loaded (state is set atomically).
    return { visiblePages: state.pages, filterMissed: !state.loading && state.pages.length > 0 };
  }, [visibleSectionNumbers, state.pages, state.tocEntries, state.loading]);

  // ─────────────────────────────────────────────────────────────────
  // Group chunks by page
  // ─────────────────────────────────────────────────────────────────

  const chunksByPage = useMemo(() => {
    const map = new Map<number, ChunkMetadata[]>();
    for (const chunk of chunkMap.values()) {
      if (chunk.page_num === null) continue;
      const existing = map.get(chunk.page_num);
      if (existing) {
        existing.push(chunk);
      } else {
        map.set(chunk.page_num, [chunk]);
      }
    }
    return map;
  }, [chunkMap]);

  // ─────────────────────────────────────────────────────────────────
  // Section anchors for the JP hover-driven TOC highlight
  // ─────────────────────────────────────────────────────────────────
  // Each TOC section gets a (page, y) anchor = where its heading sits in the
  // document. For jp_pdf the page text layer is JAPANESE, so we locate each
  // section's ``title_ja`` within the (eagerly-loaded) text spans of its start
  // page; on miss we fall back to the page top (y=0), which still gives
  // page-level correctness. Sorted in reading order so resolution is a simple
  // "latest anchor at-or-above the hovered block". Gated on
  // ``blockHoverTranslate`` (== the bilingual jp_pdf viewer) — other families
  // never compute this and keep the pure page-based scroll-spy.
  const sectionAnchors = useMemo(() => {
    if (!blockHoverTranslate) return [] as { id: number; page: number; y: number }[];
    const anchors: { id: number; page: number; y: number }[] = [];
    for (const e of state.tocEntries) {
      if (e.page_start === null) continue; // absent template sections have no page
      const ja = (e.title_ja ?? '').replace(/\s+/g, '');
      const spans = textSpansCache.get(e.page_start);
      let y = 0;
      if (ja && spans && spans.length) {
        const found = findHeadingYByJa(spans, ja);
        if (found !== null) y = found;
      }
      anchors.push({ id: e.id, page: e.page_start, y });
    }
    anchors.sort((a, b) => (a.page !== b.page ? a.page - b.page : a.y - b.y));
    return anchors;
  }, [blockHoverTranslate, state.tocEntries, textSpansCache]);

  // Resolve a hovered block at (page, y0) to the section it belongs to: the
  // latest heading anchor at-or-above it in reading order. A small epsilon
  // absorbs the sub-pixel gap between a heading block's own top and the y we
  // located for its title, so hovering a heading resolves to its own section.
  const resolveSectionAt = useCallback(
    (page: number, y0: number): number | null => {
      let best: number | null = null;
      for (const a of sectionAnchors) {
        if (a.page < page || (a.page === page && a.y <= y0 + 0.01)) {
          best = a.id;
        } else if (a.page > page) {
          break; // sorted: no later anchor can qualify
        }
      }
      return best;
    },
    [sectionAnchors],
  );

  // Reference-guided hover: resolve the hovered box (page + y0) to its section
  // SYNCHRONOUSLY at the translate call, then hand that section_id to the JP
  // translate closure so the hover reuses the section's authoritative stored
  // English (search↔display parity). Resolved here, not from a hover-state ref,
  // because translate fires in the layer's onMouseEnter BEFORE onHover's effect
  // runs — a ref would be one box stale. undefined for non-JP families (no
  // blockHoverTranslate → no anchors → unchanged behavior).
  const sectionAwareTranslate = useMemo(
    () =>
      blockHoverTranslate
        ? (ja: string, preserveHtml?: boolean, page?: number, y0?: number) =>
            blockHoverTranslate(
              ja,
              preserveHtml,
              page != null && y0 != null ? resolveSectionAt(page, y0) : null,
            )
        : undefined,
    [blockHoverTranslate, resolveSectionAt],
  );

  // Hover bridge: forward the region to the parent's side panel AND, for
  // jp_pdf, drive the section-precise TOC highlight from the hovered block.
  // Keeps the last section on un-hover (region === null) so moving the cursor
  // off the page to read the panel doesn't blank the TOC.
  const handleBlockHover = useCallback(
    (region: import('./PageRenderer').JpHoverRegion | null) => {
      onBlockHover?.(region);
      if (!blockHoverTranslate) return;
      if (region && region.page != null && region.y0 != null) {
        setHoverSectionId(resolveSectionAt(region.page, region.y0));
      }
    },
    [onBlockHover, blockHoverTranslate, resolveSectionAt],
  );

  // Pure scrolling (no hover) reverts the TOC to the page-based active
  // section — clear the hover override whenever the visible page changes so
  // the two interactions don't fight (hover = precise; scroll = page baseline).
  useEffect(() => {
    setHoverSectionId(null);
  }, [currentPage]);

  // ─────────────────────────────────────────────────────────────────
  // Page ref callback factory
  // ─────────────────────────────────────────────────────────────────

  // Per-page ref callbacks, memoized by page number. Returning a fresh
  // ``(el) => …`` closure on every render made ``observerRef`` a new prop each
  // time, which defeated ``React.memo`` on PageRenderer — so EVERY scroll
  // (setCurrentPage), hover, and background fetch re-rendered all mounted pages
  // (400+ on a JP interview form → visible scroll lag). Caching one stable
  // closure per pageNum keeps the prop identity fixed so memo actually holds.
  const pageRefCallbacks = useRef<
    Map<number, (el: HTMLDivElement | null) => void>
  >(new Map());
  const getPageRefCallback = useCallback((pageNum: number) => {
    let cb = pageRefCallbacks.current.get(pageNum);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        if (el) {
          pageRefsMap.current.set(pageNum, el);
        } else {
          pageRefsMap.current.delete(pageNum);
        }
      };
      pageRefCallbacks.current.set(pageNum, cb);
    }
    return cb;
  }, []);

  // Merge the active sidebar-search phrase into the page highlight overlay so
  // a search match is boxed on the page like Ctrl+F, alongside any
  // compare/keyword terms passed via props. The search phrase carries no row
  // color, so it falls through to the default amber <mark>.
  const effHighlightTerms = useMemo<string[] | null>(() => {
    const base =
      highlightTerms && highlightTerms.length
        ? highlightTerms
        : highlightTerm
          ? [highlightTerm]
          : [];
    const merged = searchTerm ? [...base, searchTerm, ...matchedTerms] : base;
    return merged.length ? merged : null;
  }, [highlightTerms, highlightTerm, searchTerm, matchedTerms]);

  // Deterministic section-highlight boxes grouped by page (EU-historical path).
  // Normalized [x0,y0,x1,y1]; drawn by PageRenderer as a translucent overlay —
  // the reliable highlight when there are no chunk_ids and word-level text
  // matching can't hit a multi-word section phrase.
  const sectionBoxesByPage = useMemo<Map<number, [number, number, number, number][]>>(() => {
    const m = new Map<number, [number, number, number, number][]>();
    for (const s of sectionSpans ?? []) {
      if (!s || !s.page || !Array.isArray(s.box) || s.box.length < 4) continue;
      const arr = m.get(s.page) ?? [];
      arr.push(s.box);
      m.set(s.page, arr);
    }
    return m;
  }, [sectionSpans]);

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
          <p className="mt-3 text-sm text-gray-500">Loading document viewer...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center max-w-md">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
          <p className="mt-3 text-sm text-red-600">{state.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="flex h-full overflow-hidden"
      onMouseEnter={() => { pointerInsideRef.current = true; }}
      onMouseLeave={() => { pointerInsideRef.current = false; }}
    >
      {/* TOC Sidebar — hidden for NICE HTA demo docs (showToc=false),
          collapsible everywhere else so a narrow compare panel can give the
          page image the full width. */}
      {showToc && !tocOpen && (
        <button
          onClick={() => setTocOpen(true)}
          className="w-8 flex-shrink-0 border-r border-slate-200 bg-white flex items-start justify-center pt-3 text-slate-400 hover:text-slate-600 hover:bg-slate-50"
          title="Show contents"
          aria-label="Show contents"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      )}
      {showToc && tocOpen && (
      <aside className="w-64 flex-shrink-0 border-r border-slate-200 flex flex-col overflow-hidden bg-white">
        <div className="flex justify-end px-1 pt-1">
          <button
            onClick={() => setTocOpen(false)}
            className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            title="Hide contents"
            aria-label="Hide contents"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>
        <DocumentSearch
          docId={docId}
          onNavigate={handleSearchNavigate}
          // Monolingual families search the page text layer client-side
          // (exactly what Ctrl+F sees).  Bilingual jp_pdf (signalled by
          // ``blockHoverTranslate``) has a JAPANESE page layer but an
          // ENGLISH reading surface, so route to the server search, which
          // queries the translated ``chunk_text`` and returns page-based
          // hits — otherwise an English query over Japanese spans finds
          // nothing.
          searchFn={blockHoverTranslate ? jpSearchDocument : localSearchDocument}
          onActiveQueryChange={(t) => {
            if (!t) { matchedTermsRef.current = new Set(); setMatchedTerms([]); }
            setSearchTerm(t); onActiveSearchTermChange?.(t);
          }}
          inputRef={searchInputRef}
        />
        <div className="flex-1 overflow-hidden">
          <TOCSidebar
            entries={state.tocEntries}
            onNavigate={handleTOCNavigate}
            currentPage={currentPage}
            // jp_pdf: hover-resolved section wins; null falls back to the
            // page-based active section inside TOCSidebar.
            // A miss highlights the section's own row so the user can see where
            // it sits and click it themselves.
            activeSectionOverride={hoverSectionId ?? null}
          />
        </div>
      </aside>
      )}

      {/* Scrollable page viewer */}
      <div
        ref={scrollContainerRef}
        className="relative flex-1 overflow-y-auto px-4 py-4 bg-gray-50"
      >
        <div className="max-w-4xl mx-auto">
          {filterMissed && (
            <div
              className="mb-2 rounded border border-amber-300 bg-amber-100 px-3 py-2 text-sm text-amber-950"
              role="alert"
            >
              The selected section(s) couldn't be located in this PDF — showing the full document.
            </div>
          )}
          {visiblePages.map((page) => (
            <DisplayOnlyPageFrame
              key={page.page_num}
              displayMode={state.displayMode}
              displayNotice={state.displayNotice}
            >
              <PageRenderer
                page={page}
                pageNum={page.page_num}
                renderOverlays={
                  visiblePages.length <= OVERLAY_VIRT_THRESHOLD ||
                  Math.abs(page.page_num - currentPage) <= OVERLAY_WINDOW
                }
                textSpans={textSpansCache.get(page.page_num)}
                chunks={chunksByPage.get(page.page_num) ?? EMPTY_CHUNKS}
                chunkAnchors={chunkAnchors}
                pageAnchors={pageAnchors?.[page.page_num]}
                highlightTerm={highlightTerm}
                highlightTerms={effHighlightTerms}
                highlightColors={highlightColors}
                englishExact={englishExact}
                sectionBoxes={sectionBoxesByPage.get(page.page_num)}
                headingBox={
                  headingFlash && headingFlash.page === page.page_num
                    ? headingFlash.box
                    : undefined
                }
                headingFlashKey={
                  headingFlash && headingFlash.page === page.page_num
                    ? headingFlash.key
                    : undefined
                }
                activeChunkId={activeChunkId}
                onChunkClick={handleChunkClick}
                observerRef={getPageRefCallback(page.page_num)}
                onZoomClick={handleZoomClick}
                blockHoverTranslate={sectionAwareTranslate}
                onBlockHover={handleBlockHover}
                autoSelect={autoSelect}
                onAutoSelected={clearAutoSelect}
              />
            </DisplayOnlyPageFrame>
          ))}
        </div>
      </div>

      {/* Zoom lightbox — re-renders the SAME PageRenderer for the zoomed page
          (page image + keyword <mark> term overlays + ChunkHighlight bbox) so
          highlights scale with the page and stay aligned at every zoom level.
          Prev/Next steps through ALL the doc's pages. */}
      {zoomIdx !== null && state.pages[zoomIdx] && (
        <PageZoomLightbox
          pageCount={state.pages.length}
          index={zoomIdx}
          onIndex={setZoomIdx}
          onClose={() => setZoomIdx(null)}
          pageLabel={(i) => `Page ${state.pages[i]!.page_num}`}
          renderPage={(i) => {
            const zp = state.pages[i]!;
            return (
              <DisplayOnlyPageFrame
                displayMode={state.displayMode}
                displayNotice={state.displayNotice}
              >
                <PageRenderer
                  key={zp.page_num}
                  page={zp}
                  pageNum={zp.page_num}
                  textSpans={textSpansCache.get(zp.page_num)}
                  chunks={chunksByPage.get(zp.page_num) ?? []}
                  chunkAnchors={chunkAnchors}
                  pageAnchors={pageAnchors?.[zp.page_num]}
                  highlightTerm={highlightTerm}
                  highlightTerms={effHighlightTerms}
                  highlightColors={highlightColors}
                englishExact={englishExact}
                  sectionBoxes={sectionBoxesByPage.get(zp.page_num)}
                  activeChunkId={activeChunkId}
                  onChunkClick={handleChunkClick}
                  blockHoverTranslate={sectionAwareTranslate}
                  onBlockHover={handleBlockHover}
                />
              </DisplayOnlyPageFrame>
            );
          }}
        />
      )}
    </div>
  );
});

/** One instance per doc identity (API family + doc id). Every page-keyed cache
 *  and in-flight fetch belongs to ONE doc; resetting in place is not enough — a
 *  reply still in flight from the previous identity lands after the reset and
 *  poisons the new cache. Prod 2026-11-02: it_pdf doc 700 drew de_pdf doc 700's
 *  boxes. A fresh instance makes the stale reply write into a dead one. */
const RegulatoryViewerEmbed = forwardRef<RegulatoryViewerHandle, RegulatoryViewerEmbedProps>(
  function RegulatoryViewerEmbed(props, ref) {
    return <RegulatoryViewerEmbedInner key={`${props.viewerApi.prefix}:${props.docId}`} ref={ref} {...props} />;
  },
);

export default RegulatoryViewerEmbed;

// ─────────────────────────────────────────────────────────────────────
// Heading-anchor search
// ─────────────────────────────────────────────────────────────────────

/** Punctuation- and whitespace-free form, for comparisons that must survive
 *  a text layer emitting heading glyphs out of x-order. */
const squashHeading = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** How many consecutive visual lines a single wrapped heading may occupy. */
const MAX_WRAPPED_HEADING_LINES = 3;

/**
 * Text-span y-coordinates are expected as a 0–1 fraction of page height, but
 * the ``gemini_ocr`` extraction path persists raw PDF points (0–792 for
 * Letter).  A point value used as a fraction scrolls hundreds of page-heights
 * past the target, so convert on the way out; also clamp, because the native
 * tier can emit a slightly negative y0 for a span above the page origin.
 *
 * Detection is by magnitude: a legitimate normalized y never exceeds 1.
 */
export function normalizeSpanY(
  rawY: number,
  pdfHeightPt: number | null | undefined,
): number {
  const converted =
    rawY > 1 && pdfHeightPt && pdfHeightPt > 0 ? rawY / pdfHeightPt : rawY;
  return Math.min(1, Math.max(0, converted));
}

/**
 * Find the Y-coordinate of a section heading within a page's text spans, in
 * the spans' own coordinate space (the caller normalizes via
 * ``normalizeSpanY``).  Returns null on miss, in which case the caller falls
 * back to page-top scroll.
 *
 * Exported for unit testing, like ``sectionInToc`` / ``planLanding``.
 */
/** Bilingual in-document search: ``'byScript'`` — a source-script query
 *  searches the page text, anything else the English (JP, zh); ``'both'`` —
 *  search the page text AND the English, one row per page (Latin sources). */
export type DocSearchMode = 'byScript' | 'both';

/**
 * One row per page from a Latin-source bilingual search: the ORIGINAL-text hit
 * wins on a page both matched; each row is labelled with the language that
 * matched. Exported for unit testing.
 */
export function mergeBilingualSearch(
  docId: number, query: string, src: SearchResult[], en: SearchResult[],
): SearchResponse {
  const tag = (label: string) =>
    `<span style="font-size:10px;font-weight:600;color:#64748b;margin-right:4px">${label}</span>`;
  const byPage = new Map<number, SearchResult>();
  for (const r of src) byPage.set(r.page_num, { ...r, headline: tag('Original') + r.headline });
  for (const r of en) {
    if (!byPage.has(r.page_num)) byPage.set(r.page_num, { ...r, headline: tag('English') + r.headline });
  }
  const results = [...byPage.values()].sort((a, b) => a.page_num - b.page_num);
  return { document_id: docId, query, total_results: results.length, results };
}

/** Normalized (0-1) box of one matched heading line: [x0, y0, x1, y1]. */
export interface HeadingLine { x0: number; y0: number; x1: number; y1: number }

/**
 * Locate a section heading in a page's text spans and return the BOX of the
 * matched line — what the section-jump highlight is drawn from.
 *
 * Null on a miss, and a miss must stay a miss: the jump then scrolls to the
 * page without a box. Drawing a box on a line we did not
 * positively match is the failure mode line-level highlighting is withheld
 * over, so there is deliberately no "closest guess" branch here.
 */
export function findHeadingLine(
  spans: TextSpan[],
  sectionNumber: string | null,
  title: string,
): HeadingLine | null {
  if (!spans.length) return null;

  const normalize = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
  const titleNorm = normalize(title);
  if (!titleNorm) return null;
  const titleSquashed = squashHeading(title);
  if (!titleSquashed) return null;

  const numMatch = sectionNumber
    ? sectionNumber.match(/(\d+(?:\.\d+)*)$/)
    : null;
  const shortNum = numMatch?.[1] ?? sectionNumber ?? '';
  const fullHeading = normalize(
    shortNum ? `${shortNum} ${title}` : title,
  );

  // Aggregate spans into visual lines.  Sort is stable in modern JS,
  // so spans within the same LINE_EPSILON band keep relative x order.
  const LINE_EPSILON = 0.005;
  const sorted = [...spans].sort((a, b) =>
    a.y0 !== b.y0 ? a.y0 - b.y0 : a.x0 - b.x0,
  );

  // Each line keeps the union of its spans' boxes, so the highlight covers the
  // whole heading ("7" + "WARNINGS AND PRECAUTIONS" are two spans, one line).
  type Line = { y0: number; text: string; x0: number; x1: number; y1: number };
  const lines: Line[] = [];
  for (const span of sorted) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(span.y0 - last.y0) < LINE_EPSILON) {
      last.text += ' ' + span.text;
      last.x0 = Math.min(last.x0, span.x0);
      last.x1 = Math.max(last.x1, span.x1);
      last.y1 = Math.max(last.y1, span.y1);
    } else {
      lines.push({ y0: span.y0, text: span.text, x0: span.x0, x1: span.x1, y1: span.y1 });
    }
  }
  const box = (l: Line): HeadingLine => ({ x0: l.x0, y0: l.y0, x1: l.x1, y1: l.y1 });

  // 1. Prefer exact "{number} {title}" match (most specific).
  for (const line of lines) {
    const lineNorm = normalize(line.text);
    if (lineNorm === fullHeading || lineNorm.startsWith(fullHeading)) {
      return box(line);
    }
  }

  // 2. Punctuation-insensitive whole-line match.  FDA text layers can emit a
  //    heading's glyphs out of x-order — "Approval & Labeling" arrives as
  //    "APPROVAL LABELING &" — which defeats substring matching.
  for (const line of lines) {
    if (squashHeading(line.text) === titleSquashed) return box(line);
  }

  // 3. Wrapped heading.  An all-caps FDA banner routinely spans two visual
  //    lines ("SUPPLEMENT ACKNOWLEDGMENT AND" / "APPROVAL") and the
  //    segmenter joins it into one stored title, so no single line can ever
  //    contain the whole thing.  A window may only START on a line that is
  //    itself a prefix of the title — otherwise the anchor drifts up to the
  //    prose line above the heading.
  for (let i = 0; i < lines.length; i++) {
    const startLine = lines[i];
    if (!startLine) continue;
    const head = squashHeading(startLine.text);
    if (!head || head.length >= titleSquashed.length) continue;
    if (!titleSquashed.startsWith(head)) continue;

    let acc = head;
    const limit = Math.min(i + MAX_WRAPPED_HEADING_LINES, lines.length);
    for (let j = i + 1; j < limit; j++) {
      const nextLine = lines[j];
      if (!nextLine) break;
      acc += squashHeading(nextLine.text);
      if (acc.startsWith(titleSquashed)) return box(startLine);
      // Stop as soon as the accumulation diverges from the title.
      if (!titleSquashed.startsWith(acc)) break;
    }
  }

  // 4. Fallback: a line containing the title.  Guarded by length — the
  //    segmenter's synthetic pre-banner title ("Letter") appears in no
  //    document as a heading, and an unguarded substring test matches
  //    ordinary prose ("...no later than 14 days from the date of this
  //    letter..."), scrolling to an arbitrary spot.  Landing at the page top
  //    is the honest answer; a confidently wrong anchor is not.
  for (const line of lines) {
    const lineSquashed = squashHeading(line.text);
    if (!lineSquashed.includes(titleSquashed)) continue;
    if (lineSquashed.length > titleSquashed.length * 2 + 20) continue;
    return box(line);
  }

  return null;
}

/** The y-only view of ``findHeadingLine`` — the scroll anchor. */
export function findHeadingY(
  spans: TextSpan[],
  sectionNumber: string | null,
  title: string,
): number | null {
  return findHeadingLine(spans, sectionNumber, title)?.y0 ?? null;
}

/**
 * Locate a JAPANESE section heading within a page's text spans and return its
 * normalized (0-1) top Y, or null on miss.  jp_pdf pages have a Japanese text
 * layer, so the TOC's English ``title`` won't match — we match the section's
 * ``title_ja`` (whitespace already stripped by the caller) against the spans.
 *
 * Spans are aggregated into visual lines (same y0-band logic as ``findHeadingY``)
 * and compared with all whitespace removed, since PMDA headings are often split
 * across spans and Japanese carries no word spaces.  Returns the FIRST (topmost)
 * line that contains the heading — a section's heading is the first place its
 * title text appears on its start page, so the topmost match is the heading
 * itself, not a later body mention.
 */
function findHeadingYByJa(spans: TextSpan[], titleJaStripped: string): number | null {
  if (!spans.length || !titleJaStripped) return null;

  const LINE_EPSILON = 0.005;
  const sorted = [...spans].sort((a, b) =>
    a.y0 !== b.y0 ? a.y0 - b.y0 : a.x0 - b.x0,
  );

  type Line = { y0: number; text: string };
  const lines: Line[] = [];
  for (const span of sorted) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(span.y0 - last.y0) < LINE_EPSILON) {
      last.text += span.text;
    } else {
      lines.push({ y0: span.y0, text: span.text });
    }
  }

  for (const line of lines) {
    if (line.text.replace(/\s+/g, '').includes(titleJaStripped)) {
      return line.y0;
    }
  }
  return null;
}
